import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
} from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import { InfoTooltip } from '@/components/ui/Tooltip';
import { getOffer } from '../fixtures/sourceCatalog';
import { IntegrationDemo } from '../demoStore';
import { DataSource, DataTypeKey, MatchKey } from '../types';

const MATCH_KEYS: MatchKey[] = ['workEmail', 'employeeNumber', 'nationalId'];

interface FieldMappingPanelProps {
  dataType: DataTypeKey | null;
  source: DataSource | undefined;
  demo: IntegrationDemo;
  onClose: () => void;
}

const FieldMappingPanel: React.FC<FieldMappingPanelProps> = ({
  dataType, source, demo, onClose,
}) => {
  const { t } = useTranslation();
  if (!dataType) return null;

  // A system that does not expose this data type has no fields to map.
  const offer = source ? getOffer(source.system, dataType) : undefined;
  if (!offer) return null;

  // Captured so the handlers below keep the narrowed type inside their closures.
  const activeType = dataType;
  const point = demo.state.points.find((p) => p.dataType === activeType);
  const mapping = point?.mapping ?? [];
  const matchKey = point?.matchKey;

  const setSourceField = (targetField: string, sourceField: string) => {
    demo.updateMapping(
      activeType,
      mapping.map((m) => (m.targetField === targetField
        ? { ...m, sourceField: sourceField === '' ? null : sourceField }
        : m)),
    );
  };

  const setValueTarget = (targetField: string, sourceValue: string, targetValue: string) => {
    demo.updateMapping(
      activeType,
      mapping.map((m) => (m.targetField === targetField
        ? {
          ...m,
          valueMap: m.valueMap?.map((v) => (v.sourceValue === sourceValue
            ? { ...v, targetValue: targetValue === '' ? null : targetValue }
            : v)),
        }
        : m)),
    );
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="mapping-panel">
        <DialogHeader>
          <DialogTitle>{t('extInt.mapping.title', { entity: t(`extInt.dataType.${dataType}`) })}</DialogTitle>
          <DialogDescription>{t('extInt.mapping.description')}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <p
            className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
            data-testid="mapping-source-note"
          >
            {t('extInt.mapping.sourceNote', { source: source?.displayName ?? '' })}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 text-start font-medium">{t('extInt.mapping.sourceField')}</th>
                  <th className="py-2 text-start font-medium">{t('extInt.mapping.targetField')}</th>
                  <th className="py-2 text-start font-medium">{t('extInt.mapping.state')}</th>
                </tr>
              </thead>
              <tbody>
                {mapping.map((field) => {
                  const unmappedValues = field.valueMap?.filter((v) => v.targetValue === null) ?? [];
                  return (
                    <tr key={field.targetField} className="border-b border-border last:border-0" data-testid={`mapping-row-${field.targetField}`}>
                      <td className="py-2.5 pe-4">
                        <select
                          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                          value={field.sourceField ?? ''}
                          onChange={(e) => setSourceField(field.targetField, e.target.value)}
                          data-testid={`mapping-select-${field.targetField}`}
                        >
                          <option value="">{t('extInt.mapping.unmapped')}</option>
                          {offer.sourceFields.map((sf) => (
                            <option key={sf} value={sf}>{sf}</option>
                          ))}
                        </select>
                        {field.sourceFormatHint && (
                          <span className="mt-1 block text-xs text-muted-foreground">
                            {t('extInt.mapping.formatHint', { format: field.sourceFormatHint })}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pe-4">
                        <span className="font-medium text-foreground">{t(field.targetLabelKey)}</span>
                        {field.required && (
                          <span className="ms-2 text-xs text-muted-foreground">{t('extInt.mapping.required')}</span>
                        )}
                      </td>
                      <td className="py-2.5">
                        {field.required && !field.sourceField ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
                            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('extInt.mapping.blocking')}
                          </span>
                        ) : unmappedValues.length > 0 ? (
                          <div className="space-y-1">
                            {unmappedValues.map((v) => (
                              <div key={v.sourceValue} className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{v.sourceValue}</span>
                                <input
                                  type="text"
                                  className="w-32 rounded-md border border-border bg-background px-2 py-1 text-xs"
                                  placeholder={t('extInt.mapping.valuePlaceholder')}
                                  onBlur={(e) => setValueTarget(field.targetField, v.sourceValue, e.target.value)}
                                  data-testid={`value-map-${field.targetField}-${v.sourceValue}`}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('extInt.mapping.mapped')}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {dataType === 'employees' && (
            <div className="rounded-md border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-foreground">{t('extInt.mapping.matchKey')}</h3>
                <InfoTooltip content={t('extInt.mapping.matchKeyTooltip')} data-testid="match-key-tooltip" />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {MATCH_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => demo.setMatchKey(activeType, key)}
                    className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      matchKey === key
                        ? 'border-primary bg-primary/10 font-medium text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`match-key-${key}`}
                  >
                    {t(`extInt.mapping.matchKey.${key}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} data-testid="mapping-done">{t('extInt.mapping.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FieldMappingPanel;
